import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useToast } from '../common/Toast';
import { useConfirm } from '../common/ConfirmDialog';
import { getAutopilotHistory, deployAutopilot } from '../../api/autopilot';
import { AUTOPILOT_STATUS_LABEL } from '../../constants/autopilot';
import './AutopilotProjectPanel.scss';

// 對齊 WorkItemDetailDrawer.jsx 的 fmtTime 寫法(這個 repo 目前沒有共用的日期格式 util)
const fmtTime = (d) => (d ? new Date(d).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : '');

const ItemRow = ({ r }) => (
  <tr>
    <td>{r.spec?.slice(0, 40) || '—'}</td>
    <td><span className={`ap-status ap-status--${r.status}`}>{AUTOPILOT_STATUS_LABEL[r.status] || r.status}</span></td>
    <td>{fmtTime(r.created_at)}</td>
  </tr>
);

/**
 * 專案層級的 Autopilot 面板:AI 修復歷史紀錄 + 部署到 staging。
 * 用 portal 掛到 document.body——這個 repo 好幾個檔案各自定義同名的 .modal-overlay,
 * 疊在一起蓋不到側邊欄(cascade 順序不保證);掛 body 直接繞開這個問題,不用跟別人比 z-index。
 */
const AutopilotProjectPanel = ({ isOpen, onClose, project }) => {
  const toast = useToast();
  const { confirm } = useConfirm();
  const projectPath = project?.autopilot_config?.gitlabProjectPath;
  const [items, setItems] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const alive = useRef(true);

  useEffect(() => {
    if (!isOpen || !projectPath) return;
    alive.current = true;
    let first = true; // 只有第一次進來才顯示「載入中」,之後每 5 秒靜默更新,不要一直閃
    const poll = async () => {
      if (first) setLoading(true);
      const { data, error } = await getAutopilotHistory(projectPath);
      if (!alive.current) return;
      if (!error) { setItems(data.items || []); setDeployments(data.deployments || []); }
      if (first) setLoading(false);
      first = false;
    };
    poll();
    const timer = setInterval(poll, 5000);
    return () => { alive.current = false; clearInterval(timer); };
  }, [isOpen, projectPath]);

  // 按 deployment_id 分組:每次部署收合成一筆,展開才看到那次帶走的工作項目;
  // 還沒被部署帶走的(null)平鋪列在最上面,那些才是使用者現在最關心的。
  const { pendingItems, deployedGroups } = useMemo(() => {
    // 失敗的不進部署列表(部署面板只該看到可部署的);失敗原因/重試都在該 work_item 的抽屜裡。
    const pending = items.filter((r) => !r.deployment_id && r.status !== 'failed');
    const groups = deployments
      .map((dep) => ({ deployment: dep, items: items.filter((r) => r.deployment_id === dep.id) }))
      .filter((g) => g.items.length > 0);
    return { pendingItems: pending, deployedGroups: groups };
  }, [items, deployments]);

  const toggleExpand = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  if (!isOpen) return null;

  const committedCount = pendingItems.filter((r) => r.status === 'committed').length;

  const handleDeploy = async () => {
    const ok = await confirm({
      title: '部署到 staging',
      message: committedCount > 0
        ? `會把 ${committedCount} 筆已改完的修復整包部署到 staging。確定?`
        : '目前沒有已改完待部署的修復,仍要重新部署目前 main 上的內容?',
      confirmText: '部署',
    });
    if (!ok) return;
    setDeploying(true);
    const { data, error } = await deployAutopilot(projectPath, { versionFile: project?.autopilot_config?.versionFile });
    setDeploying(false);
    if (error) { toast.error(error); return; }
    if (data.bumpSkippedReason) toast.error(`版本號沒 bump 到:${data.bumpSkippedReason}(部署仍繼續)`);
    else toast.success(`已部署,${data.deployedCount} 筆修復隨這次上線`);
  };

  return ReactDOM.createPortal(
    <div className="ap-modal-overlay" onClick={onClose}>
      <div className="ap-panel" onClick={(e) => e.stopPropagation()}>
        <div className="ap-modal-header">
          <h2>Autopilot・{project?.name}</h2>
          <button className="ap-close-btn" onClick={onClose}>&times;</button>
        </div>
        <div className="ap-modal-body">
          {!projectPath ? (
            <div className="ap-empty">這個專案還沒設定 autopilot_config。</div>
          ) : (
            <>
              <div className="ap-toolbar">
                <span className="ap-count">共 {items.length} 筆 AI 修復紀錄</span>
                <button className="ap-deploy-btn" onClick={handleDeploy} disabled={deploying}>
                  {deploying ? '部署中…' : '🚀 部署到 staging'}
                </button>
              </div>
              {loading ? (
                <div className="ap-empty">載入中…</div>
              ) : items.length === 0 ? (
                <div className="ap-empty">還沒有任何 AI 修復紀錄</div>
              ) : (
                <>
                  {pendingItems.length > 0 && (
                    <table className="ap-table">
                      <thead><tr><th>需求內容</th><th>狀態</th><th>時間</th></tr></thead>
                      <tbody>{pendingItems.map((r) => <ItemRow r={r} key={r.id} />)}</tbody>
                    </table>
                  )}
                  {deployedGroups.map(({ deployment, items: groupItems }) => {
                    const isOpen2 = expanded.has(deployment.id);
                    return (
                      <div className="ap-deploy-group" key={deployment.id}>
                        <button className="ap-deploy-group-head" onClick={() => toggleExpand(deployment.id)}>
                          <span className={`ap-chevron ${isOpen2 ? 'open' : ''}`}>▸</span>
                          <span>部署於 {fmtTime(deployment.created_at)} · {deployment.short_sha || deployment.sha?.slice(0, 8)}</span>
                          <span className="ap-deploy-group-count">{groupItems.length} 筆</span>
                          {deployment.pipeline_url && (
                            <a href={deployment.pipeline_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                              pipeline ↗
                            </a>
                          )}
                        </button>
                        {isOpen2 && (
                          <table className="ap-table">
                            <thead><tr><th>需求內容</th><th>狀態</th><th>時間</th></tr></thead>
                            <tbody>{groupItems.map((r) => <ItemRow r={r} key={r.id} />)}</tbody>
                          </table>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AutopilotProjectPanel;
